/**
 * User Modal Component - Matches User Management modal design
 * Displays user profile details, site permissions, and quick actions
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Loader2,
  Mail,
  Phone,
  Building2,
  UserCog,
  Check,
  X,
  Power,
  ShieldCheck,
  Eye,
  Bell,
  FileCheck,
  CheckCircle,
  MapPin,
  Calendar,
  User as UserIcon,
  Pencil,
  KeyRound,
} from 'lucide-react';
import { UserProfile, UserSitePermission } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface UserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  userModalTab: 'profile' | 'sites';
  onTabChange: (tab: 'profile' | 'sites') => void;
  userSitePermissions: UserSitePermission[];
  permissionsLoading: boolean;
  savingPermission: number | null;
  impersonating: number | null;
  isHelixAdmin: boolean;
  isCustomer: boolean;
  /** CR-5857: customer admin (or helix admin) — gates Edit + Reset Password. */
  isCustomerAdmin: boolean;
  // CR-4858: The company the modal was opened from (the access context).
  // Multi-company users otherwise render with their primary company here,
  // which misleads admins triaging a specific supplier's user list.
  contextCompanyName?: string;
  onToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  onUpdatePermission: (siteId: number, field: string, value: boolean) => Promise<void>;
  onImpersonate: (userProfile: UserProfile) => Promise<void>;
  /** CR-5857: open the Edit Profile dialog for this user. */
  onEditUser: (userProfile: UserProfile) => void;
  /** CR-5857: trigger an admin-driven password reset email. */
  onResetPassword: (userProfile: UserProfile) => void;
}

export function UserModal({
  open,
  onOpenChange,
  selectedUser,
  userModalTab,
  onTabChange,
  userSitePermissions,
  permissionsLoading,
  savingPermission,
  impersonating,
  isHelixAdmin,
  isCustomer,
  isCustomerAdmin,
  contextCompanyName,
  onToggleSiteLink,
  onUpdatePermission,
  onImpersonate,
  onEditUser,
  onResetPassword,
}: UserModalProps) {
  const { formatDateTime } = useFormatters();
  if (!selectedUser) return null;
  // CR-5857: Edit + Reset Password are reserved for helix admins and
  // customer admins managing supplier users. Same predicate as UsersTab.
  const canManageProfile = isHelixAdmin || isCustomerAdmin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Details
          </DialogTitle>
          <DialogDescription>
            {selectedUser.user.full_name || selectedUser.user.email} • {selectedUser.user.email}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={userModalTab}
          onValueChange={(v) => onTabChange(v as 'profile' | 'sites')}
          className="flex-1 overflow-hidden flex flex-col"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile" className="gap-2">
              <UserIcon className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="sites" className="gap-2">
              <MapPin className="h-4 w-4" />
              Related Companies
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="flex-1 overflow-y-auto mt-4 space-y-6">
            {/* Status & Actions Bar */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                {/* CR-4551 round 4: same Invite Sent badge surface as the
                    UsersTab card so the modal doesn't contradict the list. */}
                {!selectedUser.user.is_active ? (
                  <Badge variant="secondary">
                    <X className="h-3 w-3 mr-1" />
                    Inactive
                  </Badge>
                ) : selectedUser.user.invitation_sent && !selectedUser.user.has_joined ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    Invite Sent
                  </Badge>
                ) : (
                  <Badge variant="default" className="bg-green-500">
                    <Check className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                )}
                {!selectedUser.is_approved && (
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    Pending Approval
                  </Badge>
                )}
                {selectedUser.role_details?.is_admin_role && (
                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Admin role
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 items-center">
                {canManageProfile && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditUser(selectedUser)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit Profile
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onResetPassword(selectedUser)}
                      disabled={!selectedUser.user.is_active}
                    >
                      <KeyRound className="h-4 w-4 mr-1" />
                      Reset Password
                    </Button>
                  </>
                )}
                {(isHelixAdmin || isCustomer) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onImpersonate(selectedUser)}
                    disabled={
                      !selectedUser.user.is_active || impersonating === selectedUser.user.id
                    }
                  >
                    {impersonating === selectedUser.user.id ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <UserCog className="h-4 w-4 mr-1" />
                    )}
                    Login As
                  </Button>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Full Name</Label>
                <p className="text-base mt-1">{selectedUser.user.full_name || '—'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                <p className="text-base mt-1 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {selectedUser.user.email}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                <p className="text-base mt-1 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {contextCompanyName ?? selectedUser.company_name}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">User Role</Label>
                {/* div, not p: Badge renders a div, and a div inside a p is
                    invalid HTML that React reports as a hydration error. */}
                <div className="text-base mt-1">
                  <Badge variant="outline">{selectedUser.role_name || '—'}</Badge>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            {(selectedUser.phone || selectedUser.job_title || selectedUser.department) && (
              <div className="grid grid-cols-2 gap-4">
                {selectedUser.phone && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Telephone</Label>
                    <p className="text-base mt-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {selectedUser.phone}
                    </p>
                  </div>
                )}
                {selectedUser.job_title && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Position</Label>
                    <p className="text-base mt-1">{selectedUser.job_title}</p>
                  </div>
                )}
                {selectedUser.department && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                    <p className="text-base mt-1">{selectedUser.department}</p>
                  </div>
                )}
              </div>
            )}

            {/* Sites Summary */}
            {selectedUser.site_names && selectedUser.site_names.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Current Company Access</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedUser.site_names.map((site, idx) => (
                    <Badge key={idx} variant="secondary" className="gap-1">
                      <MapPin className="h-3 w-3" />
                      {site}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Created At</Label>
                <p className="text-base mt-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {formatDateTime(selectedUser.created_at)}
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Related Companies Tab */}
          <TabsContent value="sites" className="flex-1 overflow-y-auto mt-4">
            {permissionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : userSitePermissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No company permissions found</p>
                <p className="text-sm mt-2">
                  This user has no company-specific permissions configured
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userSitePermissions.map((perms) => {
                  const isSaving = savingPermission === perms.site;

                  return (
                    <Card key={perms.site} className={`${perms.is_linked ? '' : 'opacity-60'} ${isSaving ? 'animate-pulse' : ''}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {perms.site_name}
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {perms.is_linked ? 'Linked' : 'Not Linked'}
                            </span>
                            <Switch
                              checked={perms.is_linked}
                              onCheckedChange={(checked) => onToggleSiteLink(perms.site, checked)}
                              disabled={isSaving}
                            />
                          </div>
                        </div>
                        {perms.site_code && (
                          <p className="text-xs text-muted-foreground">{perms.site_code}</p>
                        )}
                      </CardHeader>

                      {perms.is_linked && (
                        <CardContent className="space-y-4">
                          {/* Active Status */}
                          <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                            <span className="text-sm font-medium flex items-center gap-2">
                              <Power className="h-4 w-4" />
                              Active at this company
                            </span>
                            <Switch
                              checked={perms.is_active}
                              onCheckedChange={(checked) =>
                                onUpdatePermission(perms.site, 'is_active', checked)
                              }
                              disabled={isSaving}
                            />
                          </div>

                          {/* Access Modules */}
                          <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Modules
                            </Label>
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <FileCheck className="h-4 w-4" />
                                  Purchasing
                                </span>
                                <Switch
                                  checked={perms.can_purchasing}
                                  onCheckedChange={(checked) =>
                                    onUpdatePermission(perms.site, 'can_purchasing', checked)
                                  }
                                  disabled={isSaving}
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  Quality
                                </span>
                                <Switch
                                  checked={perms.can_quality}
                                  onCheckedChange={(checked) =>
                                    onUpdatePermission(perms.site, 'can_quality', checked)
                                  }
                                  disabled={isSaving}
                                />
                              </div>
                              {/* CR-5340: Finance access module removed from
                                  the Related Companies popup — not relevant
                                  in this context. The can_finance field on
                                  UserSitePermission is preserved server-side
                                  for the User Management > Site Access tab,
                                  which still exposes it. */}
                            </div>
                          </div>

                          {/* CR-7076 (ADMIN-MODEL): per-site "User Role" section
                              retired. The Administrator toggle wrote a flag no
                              backend check reads (admin authority lives on the
                              user-level role) — hidden, field DORMANT. Read Only
                              survives as the Access Override. */}
                          <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Access Override
                            </Label>
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <Eye className="h-4 w-4" />
                                  Read Only
                                </span>
                                <Switch
                                  checked={perms.is_read_only}
                                  onCheckedChange={(checked) =>
                                    onUpdatePermission(perms.site, 'is_read_only', checked)
                                  }
                                  disabled={isSaving}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                View everything at this company, change nothing. Overrides their user role here.
                              </p>
                            </div>
                          </div>

                          {/* Notification Emails */}
                          <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              Notifications
                            </Label>
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <Bell className="h-4 w-4" />
                                  Procurement
                                </span>
                                <Switch
                                  checked={perms.notify_procurement}
                                  onCheckedChange={(checked) =>
                                    onUpdatePermission(perms.site, 'notify_procurement', checked)
                                  }
                                  disabled={isSaving}
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm flex items-center gap-2">
                                  <Bell className="h-4 w-4" />
                                  Quality
                                </span>
                                <Switch
                                  checked={perms.notify_quality}
                                  onCheckedChange={(checked) =>
                                    onUpdatePermission(perms.site, 'notify_quality', checked)
                                  }
                                  disabled={isSaving}
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
